import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Badge,
  Stack,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Collapse,
  CardHeader,
  CardMedia
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarToday as CalendarTodayIcon,
  Description as DescriptionIcon,
  Store as StoreIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Image as ImageIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Receipt as ReceiptIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as VisibilityIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon
} from '@mui/icons-material';

import depenseService from '../../../services/depenseService';

// Helper functions
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '0 HTG';
  return new Intl.NumberFormat('fr-HT', {
    style: 'currency',
    currency: 'HTG',
    minimumFractionDigits: 2
  }).format(amount);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d'];

const Depenses = () => {
  const [depenses, setDepenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [selectedDepense, setSelectedDepense] = useState(null);
  
  // Filter states
  const [currentTab, setCurrentTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    type_depense: '',
    fournisseur: '',
    date_debut: '',
    date_fin: '',
    montant_min: '',
    montant_max: ''
  });
  const [expandedDepenses, setExpandedDepenses] = useState([]);
  
  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Form states
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    type_depense: 'ingredients',
    montant: '',
    description: '',
    fournisseur: '',
    notes: '',
    recu: null
  });

  // Stats states
  const [stats, setStats] = useState({
    total: 0,
    moyenne: 0,
    parType: {},
    evolution: []
  });
  const [showChart, setShowChart] = useState(false); // Désactivé par défaut sans recharts

  // Type depense options
  const TYPE_DEPENSE_CHOICES = [
    { value: 'ingredients', label: 'Ingrédients', color: '#0088FE' },
    { value: 'equipement', label: 'Équipement', color: '#00C49F' },
    { value: 'entretien', label: 'Entretien/Nettoyage', color: '#FFBB28' },
    { value: 'services', label: 'Services', color: '#FF8042' },
    { value: 'salaires', label: 'Salaires', color: '#8884D8' },
    { value: 'marketing', label: 'Marketing/Publicité', color: '#82ca9d' },
    { value: 'autre', label: 'Autre', color: '#FF6B6B' }
  ];

  // Load data function
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load depenses
      const response = await depenseService.getAll();
      console.log('API Response for depenses:', response);
      
      // Gérer différents formats de réponse
      let depenseData = [];
      if (Array.isArray(response)) {
        depenseData = response;
      } else if (response && Array.isArray(response.data)) {
        depenseData = response.data;
      } else if (response && response.results && Array.isArray(response.results)) {
        depenseData = response.results;
      } else if (response && typeof response === 'object') {
        depenseData = [response];
      }
      
      console.log('Processed depenses:', depenseData);
      setDepenses(depenseData);
      
      // Calculate stats
      calculateStats(depenseData);
      
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  // Calculate statistics
  const calculateStats = (data) => {
    if (!Array.isArray(data) || data.length === 0) {
      setStats({
        total: 0,
        moyenne: 0,
        parType: {},
        evolution: []
      });
      return;
    }

    // Total dépenses
    const total = data.reduce((sum, depense) => sum + (parseFloat(depense.montant) || 0), 0);
    
    // Moyenne
    const moyenne = total / data.length;
    
    // Par type
    const parType = {};
    data.forEach(depense => {
      const type = depense.type_depense || 'autre';
      const montant = parseFloat(depense.montant) || 0;
      
      if (!parType[type]) {
        parType[type] = {
          total: 0,
          count: 0,
          label: TYPE_DEPENSE_CHOICES.find(t => t.value === type)?.label || type
        };
      }
      
      parType[type].total += montant;
      parType[type].count += 1;
    });
    
    // Evolution mensuelle (derniers 6 mois)
    const evolution = [];
    const today = new Date();
    for (let i = 5; i >= 0; i--) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthKey = date.toLocaleString('fr-FR', { month: 'long', year: 'numeric' });
      
      const monthData = data.filter(depense => {
        const depenseDate = new Date(depense.date);
        return depenseDate.getMonth() === date.getMonth() && 
               depenseDate.getFullYear() === date.getFullYear();
      });
      
      const monthTotal = monthData.reduce((sum, d) => sum + (parseFloat(d.montant) || 0), 0);
      
      evolution.push({
        mois: monthKey,
        total: monthTotal,
        count: monthData.length
      });
    }
    
    setStats({
      total,
      moyenne,
      parType,
      evolution
    });
  };

  // Initial load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // S'assurer que depenses est toujours un tableau
  const safeDepenses = Array.isArray(depenses) ? depenses : [];

  // Filter depenses
  const filteredDepenses = safeDepenses.filter(depense => {
    if (!depense) return false;
    
    const matchesSearch = searchTerm === '' || 
      (depense.description && depense.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (depense.fournisseur && depense.fournisseur.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (depense.notes && depense.notes.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesType = !filters.type_depense || depense.type_depense === filters.type_depense;
    
    const matchesFournisseur = !filters.fournisseur || 
      (depense.fournisseur && depense.fournisseur.toLowerCase().includes(filters.fournisseur.toLowerCase()));
    
    // Filtrer par date
    let matchesDate = true;
    if (filters.date_debut) {
      const filterDate = new Date(filters.date_debut);
      const depenseDate = new Date(depense.date);
      matchesDate = depenseDate >= filterDate;
    }
    if (filters.date_fin) {
      const filterDate = new Date(filters.date_fin);
      const depenseDate = new Date(depense.date);
      matchesDate = matchesDate && depenseDate <= filterDate;
    }
    
    // Filtrer par montant
    let matchesMontant = true;
    if (filters.montant_min) {
      const min = parseFloat(filters.montant_min);
      const montant = parseFloat(depense.montant) || 0;
      matchesMontant = montant >= min;
    }
    if (filters.montant_max) {
      const max = parseFloat(filters.montant_max);
      const montant = parseFloat(depense.montant) || 0;
      matchesMontant = matchesMontant && montant <= max;
    }
    
    return matchesSearch && matchesType && matchesFournisseur && matchesDate && matchesMontant;
  });

  // Sort by date (newest first)
  const sortedDepenses = [...filteredDepenses].sort((a, b) => {
    return new Date(b.date) - new Date(a.date);
  });

  // Paginated depenses
  const paginatedDepenses = sortedDepenses.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Handler functions
  const handleOpenDialog = (depense = null) => {
    if (depense) {
      setSelectedDepense(depense);
      setFormData({
        date: depense.date ? depense.date.split('T')[0] : new Date().toISOString().split('T')[0],
        type_depense: depense.type_depense || 'ingredients',
        montant: depense.montant || '',
        description: depense.description || '',
        fournisseur: depense.fournisseur || '',
        notes: depense.notes || '',
        recu: null
      });
    } else {
      setSelectedDepense(null);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        type_depense: 'ingredients',
        montant: '',
        description: '',
        fournisseur: '',
        notes: '',
        recu: null
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDepense(null);
    setFormData({
      date: new Date().toISOString().split('T')[0],
      type_depense: 'ingredients',
      montant: '',
      description: '',
      fournisseur: '',
      notes: '',
      recu: null
    });
  };

  const handleOpenDetailDialog = (depense) => {
    setSelectedDepense(depense);
    setOpenDetailDialog(true);
  };

  const handleCloseDetailDialog = () => {
    setOpenDetailDialog(false);
    setSelectedDepense(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(0); // Reset to first page when filters change
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        recu: file
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      // Validate
      if (!formData.montant || parseFloat(formData.montant) <= 0) {
        setError('Le montant doit être supérieur à 0');
        return;
      }
      
      if (!formData.description) {
        setError('La description est requise');
        return;
      }
      
      // Create FormData for file upload
      const formDataToSend = new FormData();
      
      // Ajouter tous les champs au FormData
      Object.keys(formData).forEach(key => {
        if (key === 'recu' && formData[key]) {
          formDataToSend.append(key, formData[key]);
        } else if (formData[key] !== null && formData[key] !== undefined) {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      if (selectedDepense) {
        // Update existing depense
        await depenseService.update(selectedDepense.id, formDataToSend);
        setSuccessMessage('Dépense modifiée avec succès');
      } else {
        // Create new depense
        await depenseService.create(formDataToSend);
        setSuccessMessage('Dépense créée avec succès');
      }
      
      await loadData();
      handleCloseDialog();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (err) {
      console.error('Error saving depense:', err);
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette dépense ? Cette action est irréversible.')) {
      try {
        setLoading(true);
        setError(null);
        
        await depenseService.delete(id);
        setSuccessMessage('Dépense supprimée avec succès');
        
        await loadData();
        
        setTimeout(() => setSuccessMessage(null), 3000);
        
      } catch (err) {
        setError('Erreur lors de la suppression');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleToggleExpand = (id) => {
    setExpandedDepenses(prev => 
      prev.includes(id) 
        ? prev.filter(depenseId => depenseId !== id)
        : [...prev, id]
    );
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getTypeLabel = (type) => {
    const typeObj = TYPE_DEPENSE_CHOICES.find(t => t.value === type);
    return typeObj ? typeObj.label : type;
  };

  const getTypeColor = (type) => {
    const typeObj = TYPE_DEPENSE_CHOICES.find(t => t.value === type);
    return typeObj ? typeObj.color : '#999';
  };

  const getRecuUrl = (depense) => {
    if (depense.recu) {
      if (typeof depense.recu === 'string') {
        // Si c'est une URL complète
        if (depense.recu.startsWith('http')) {
          return depense.recu;
        }
        // Si c'est un chemin relatif
        return `${process.env.REACT_APP_API_URL}${depense.recu}`;
      }
    }
    return null;
  };

  // Export functions
  const exportToCSV = () => {
    const headers = ['Date', 'Type', 'Montant', 'Description', 'Fournisseur', 'Notes'];
    const rows = sortedDepenses.map(d => [
      formatDate(d.date),
      getTypeLabel(d.type_depense),
      d.montant,
      d.description,
      d.fournisseur || '',
      d.notes || ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `depenses_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Render depense row
  const renderDepenseRow = (depense) => {
    if (!depense) return null;
    
    const isExpanded = expandedDepenses.includes(depense.id);
    const recuUrl = getRecuUrl(depense);
    
    return (
      <React.Fragment key={depense.id || Math.random()}>
        <TableRow hover>
          <TableCell>
            <IconButton
              size="small"
              onClick={() => handleToggleExpand(depense.id)}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </TableCell>
          <TableCell>
            {formatDate(depense.date)}
          </TableCell>
          <TableCell>
            <Chip
              label={getTypeLabel(depense.type_depense)}
              size="small"
              sx={{ 
                bgcolor: getTypeColor(depense.type_depense) + '20',
                color: getTypeColor(depense.type_depense),
                border: `1px solid ${getTypeColor(depense.type_depense)}`
              }}
            />
          </TableCell>
          <TableCell align="right">
            <Typography variant="body2" fontWeight="bold">
              {formatCurrency(depense.montant)}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2" noWrap>
              {depense.description}
            </Typography>
          </TableCell>
          <TableCell>
            <Box display="flex" alignItems="center" gap={1}>
              {recuUrl && (
                <IconButton
                  size="small"
                  onClick={() => window.open(recuUrl, '_blank')}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton
                size="small"
                onClick={() => handleOpenDetailDialog(depense)}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleOpenDialog(depense)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDelete(depense.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </TableCell>
        </TableRow>
        
        {/* Expanded row with details */}
        {isExpanded && (
          <TableRow>
            <TableCell colSpan={6} sx={{ bgcolor: 'background.default' }}>
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Détails
                    </Typography>
                    <Stack spacing={1}>
                      {depense.fournisseur && (
                        <Box display="flex" alignItems="center">
                          <StoreIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            Fournisseur: {depense.fournisseur}
                          </Typography>
                        </Box>
                      )}
                      {depense.enregistre_par && (
                        <Box display="flex" alignItems="center">
                          <PersonIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            Enregistré par: {depense.enregistre_par.nom || 'N/A'}
                          </Typography>
                        </Box>
                      )}
                      <Box display="flex" alignItems="center">
                        <CalendarTodayIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          Date de création: {formatDate(depense.date_creation)}
                        </Typography>
                      </Box>
                    </Stack>
                    
                    {depense.notes && (
                      <Box mt={2}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Notes
                        </Typography>
                        <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
                          <Typography variant="body2">
                            {depense.notes}
                          </Typography>
                        </Paper>
                      </Box>
                    )}
                  </Grid>
                  
                  {recuUrl && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Reçu
                      </Typography>
                      <Paper sx={{ p: 1, textAlign: 'center' }}>
                        <CardMedia
                          component="img"
                          image={recuUrl}
                          alt="Reçu de la dépense"
                          sx={{ 
                            maxHeight: 200, 
                            maxWidth: '100%',
                            objectFit: 'contain',
                            cursor: 'pointer'
                          }}
                          onClick={() => window.open(recuUrl, '_blank')}
                        />
                        <Button
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = recuUrl;
                            link.download = `recu_${depense.id}_${depense.date}.jpg`;
                            link.click();
                          }}
                          sx={{ mt: 1 }}
                        >
                          Télécharger
                        </Button>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  };

  // Loading state
  if (loading && safeDepenses.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Gestion des Dépenses
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={loading}
        >
          Nouvelle Dépense
        </Button>
      </Box>

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light' }}>
            <Typography color="text.secondary" variant="body2">Total dépenses</Typography>
            <Typography variant="h4" color="primary.dark">
              {formatCurrency(stats.total)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
            <Typography color="text.secondary" variant="body2">Moyenne par dépense</Typography>
            <Typography variant="h4" color="success.dark">
              {formatCurrency(stats.moyenne)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light' }}>
            <Typography color="text.secondary" variant="body2">Nombre de dépenses</Typography>
            <Typography variant="h4" color="info.dark">
              {safeDepenses.length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light' }}>
            <Typography color="text.secondary" variant="body2">Dépenses filtrées</Typography>
            <Typography variant="h4" color="warning.dark">
              {filteredDepenses.length}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Stats Summary (sans graphiques) */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            <BarChartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Résumé des dépenses
          </Typography>
        </Box>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Répartition par type
            </Typography>
            <Box>
              {Object.keys(stats.parType).map((type, index) => {
                const typeData = stats.parType[type];
                const percentage = stats.total > 0 ? (typeData.total / stats.total * 100).toFixed(1) : 0;
                const color = getTypeColor(type);
                
                return (
                  <Box key={type} mb={1}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2">
                        {typeData.label}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(typeData.total)} ({percentage}%)
                      </Typography>
                    </Box>
                    <Box 
                      sx={{ 
                        height: 8, 
                        width: '100%', 
                        bgcolor: 'grey.200',
                        borderRadius: 1,
                        overflow: 'hidden'
                      }}
                    >
                      <Box 
                        sx={{ 
                          height: '100%', 
                          width: `${percentage}%`, 
                          bgcolor: color,
                          borderRadius: 1
                        }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {typeData.count} dépense(s)
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Évolution mensuelle
            </Typography>
            <Box>
              {stats.evolution.map((month, index) => (
                <Box key={index} mb={1}>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2">
                      {month.mois}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(month.total)}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center">
                    <Box 
                      sx={{ 
                        flex: 1, 
                        height: 8, 
                        bgcolor: 'grey.200',
                        borderRadius: 1,
                        overflow: 'hidden',
                        mr: 1
                      }}
                    >
                      <Box 
                        sx={{ 
                          height: '100%', 
                          width: `${month.total > 0 ? Math.min((month.total / Math.max(...stats.evolution.map(m => m.total))) * 100, 100) : 0}%`, 
                          bgcolor: '#8884d8',
                          borderRadius: 1
                        }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {month.count} dépense(s)
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Export Section */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">
            Export des données
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportToCSV}
              disabled={loading || filteredDepenses.length === 0}
            >
              Exporter CSV
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => window.print()}
              disabled={loading}
            >
              Imprimer
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Rechercher description, fournisseur..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
              disabled={loading}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                name="type_depense"
                value={filters.type_depense}
                onChange={handleFilterChange}
                label="Type"
                disabled={loading}
              >
                <MenuItem value="">Tous les types</MenuItem>
                {TYPE_DEPENSE_CHOICES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              name="fournisseur"
              label="Fournisseur"
              value={filters.fournisseur}
              onChange={handleFilterChange}
              disabled={loading}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              name="montant_min"
              label="Montant min"
              type="number"
              value={filters.montant_min}
              onChange={handleFilterChange}
              InputProps={{
                startAdornment: <InputAdornment position="start">HTG</InputAdornment>,
                inputProps: { min: 0 }
              }}
              disabled={loading}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              name="montant_max"
              label="Montant max"
              type="number"
              value={filters.montant_max}
              onChange={handleFilterChange}
              InputProps={{
                startAdornment: <InputAdornment position="start">HTG</InputAdornment>,
                inputProps: { min: 0 }
              }}
              disabled={loading}
            />
          </Grid>
          
          <Grid item xs={12} md={1}>
            <Box display="flex" gap={1}>
              <Tooltip title="Actualiser">
                <IconButton onClick={loadData} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Réinitialiser les filtres">
                <IconButton 
                  onClick={() => {
                    setFilters({
                      type_depense: '',
                      fournisseur: '',
                      date_debut: '',
                      date_fin: '',
                      montant_min: '',
                      montant_max: ''
                    });
                    setSearchTerm('');
                  }}
                  disabled={loading}
                >
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
        
        {/* Date filters */}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              name="date_debut"
              label="Date de début"
              type="date"
              value={filters.date_debut}
              onChange={handleFilterChange}
              disabled={loading}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              name="date_fin"
              label="Date de fin"
              type="date"
              value={filters.date_fin}
              onChange={handleFilterChange}
              disabled={loading}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Box display="flex" gap={1} height="100%" alignItems="center">
              <Button
                variant="outlined"
                onClick={() => {
                  const today = new Date().toISOString().split('T')[0];
                  setFilters({
                    ...filters,
                    date_debut: today,
                    date_fin: today
                  });
                }}
                disabled={loading}
              >
                Aujourd'hui
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  const today = new Date();
                  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                  setFilters({
                    ...filters,
                    date_debut: firstDay.toISOString().split('T')[0],
                    date_fin: today.toISOString().split('T')[0]
                  });
                }}
                disabled={loading}
                sx={{ ml: 1 }}
              >
                Ce mois
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Depenses Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="medium">
            <TableHead>
              <TableRow>
                <TableCell width={50}></TableCell>
                <TableCell width={120}>
                  <Typography variant="subtitle2" fontWeight="bold">Date</Typography>
                </TableCell>
                <TableCell width={150}>
                  <Typography variant="subtitle2" fontWeight="bold">Type</Typography>
                </TableCell>
                <TableCell width={120} align="right">
                  <Typography variant="subtitle2" fontWeight="bold">Montant</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Description</Typography>
                </TableCell>
                <TableCell width={150} align="center">
                  <Typography variant="subtitle2" fontWeight="bold">Actions</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedDepenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {filteredDepenses.length === 0 ? 'Aucune dépense trouvée' : 'Aucune donnée à afficher'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedDepenses.map(renderDepenseRow)
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredDepenses.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Lignes par page:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
        />
      </Paper>

      {/* Summary */}
      {filteredDepenses.length > 0 && (
        <Paper sx={{ p: 2, mt: 2, bgcolor: 'primary.light' }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Résumé des dépenses filtrées
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2">
                Nombre de dépenses: <strong>{filteredDepenses.length}</strong>
              </Typography>
              <Typography variant="body2">
                Total: <strong>{formatCurrency(filteredDepenses.reduce((sum, d) => sum + (parseFloat(d.montant) || 0), 0))}</strong>
              </Typography>
              <Typography variant="body2">
                Moyenne: <strong>{formatCurrency(filteredDepenses.reduce((sum, d) => sum + (parseFloat(d.montant) || 0), 0) / filteredDepenses.length)}</strong>
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" gutterBottom>
                Répartition par type:
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {Object.keys(stats.parType).map(type => {
                  const count = filteredDepenses.filter(d => d.type_depense === type).length;
                  if (count > 0) {
                    return (
                      <Chip
                        key={type}
                        label={`${getTypeLabel(type)}: ${count}`}
                        size="small"
                        sx={{ 
                          bgcolor: getTypeColor(type) + '20',
                          color: getTypeColor(type)
                        }}
                      />
                    );
                  }
                  return null;
                })}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {selectedDepense ? 'Modifier la dépense' : 'Nouvelle dépense'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="date"
                  label="Date"
                  type="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Type de dépense</InputLabel>
                  <Select
                    name="type_depense"
                    value={formData.type_depense}
                    onChange={handleInputChange}
                    label="Type de dépense"
                    disabled={loading}
                  >
                    {TYPE_DEPENSE_CHOICES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="montant"
                  label="Montant (HTG)"
                  type="number"
                  value={formData.montant}
                  onChange={handleInputChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">HTG</InputAdornment>,
                    inputProps: { min: 0, step: 0.01 }
                  }}
                  required
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="fournisseur"
                  label="Fournisseur"
                  value={formData.fournisseur}
                  onChange={handleInputChange}
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="description"
                  label="Description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  multiline
                  rows={2}
                  placeholder="Détails de la dépense, articles achetés, etc."
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="notes"
                  label="Notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                  placeholder="Informations supplémentaires, références, etc."
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box>
                  <input
                    accept="image/*,.pdf"
                    style={{ display: 'none' }}
                    id="recu-upload"
                    type="file"
                    onChange={handleFileChange}
                  />
                  <label htmlFor="recu-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<UploadIcon />}
                      disabled={loading}
                    >
                      {formData.recu ? 'Changer le reçu' : 'Télécharger un reçu'}
                    </Button>
                  </label>
                  {formData.recu && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      {formData.recu.name}
                    </Typography>
                  )}
                  {selectedDepense && getRecuUrl(selectedDepense) && !formData.recu && (
                    <Box mt={1}>
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => window.open(getRecuUrl(selectedDepense), '_blank')}
                      >
                        Voir le reçu actuel
                      </Button>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={loading}>
              Annuler
            </Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : (selectedDepense ? 'Modifier' : 'Créer')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={openDetailDialog} onClose={handleCloseDetailDialog} maxWidth="md" fullWidth>
        {selectedDepense && (
          <>
            <DialogTitle>
              Détails de la dépense
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Informations générales
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemAvatar>
                            <CalendarTodayIcon color="action" />
                          </ListItemAvatar>
                          <ListItemText 
                            primary="Date" 
                            secondary={formatDate(selectedDepense.date)}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemAvatar>
                            <AttachMoneyIcon color="action" />
                          </ListItemAvatar>
                          <ListItemText 
                            primary="Montant" 
                            secondary={formatCurrency(selectedDepense.montant)}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemAvatar>
                            <DescriptionIcon color="action" />
                          </ListItemAvatar>
                          <ListItemText 
                            primary="Type" 
                            secondary={
                              <Chip
                                label={getTypeLabel(selectedDepense.type_depense)}
                                size="small"
                                sx={{ 
                                  bgcolor: getTypeColor(selectedDepense.type_depense) + '20',
                                  color: getTypeColor(selectedDepense.type_depense)
                                }}
                              />
                            }
                          />
                        </ListItem>
                        {selectedDepense.fournisseur && (
                          <ListItem>
                            <ListItemAvatar>
                              <StoreIcon color="action" />
                            </ListItemAvatar>
                            <ListItemText 
                              primary="Fournisseur" 
                              secondary={selectedDepense.fournisseur}
                            />
                          </ListItem>
                        )}
                        {selectedDepense.enregistre_par && (
                          <ListItem>
                            <ListItemAvatar>
                              <PersonIcon color="action" />
                            </ListItemAvatar>
                            <ListItemText 
                              primary="Enregistré par" 
                              secondary={selectedDepense.enregistre_par.nom || 'N/A'}
                            />
                          </ListItem>
                        )}
                      </List>
                    </Box>
                    
                    <Divider />
                    
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Description
                      </Typography>
                      <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                        <Typography variant="body2">
                          {selectedDepense.description}
                        </Typography>
                      </Paper>
                    </Box>
                    
                    {selectedDepense.notes && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Notes
                        </Typography>
                        <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                          <Typography variant="body2">
                            {selectedDepense.notes}
                          </Typography>
                        </Paper>
                      </Box>
                    )}
                  </Stack>
                </Grid>
                
                {getRecuUrl(selectedDepense) && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Reçu
                    </Typography>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      {getRecuUrl(selectedDepense).endsWith('.pdf') ? (
                        <Box>
                          <ReceiptIcon sx={{ fontSize: 100, color: 'primary.main' }} />
                          <Typography variant="body2" sx={{ mt: 2 }}>
                            Document PDF
                          </Typography>
                          <Button
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            onClick={() => window.open(getRecuUrl(selectedDepense), '_blank')}
                            sx={{ mt: 2 }}
                          >
                            Ouvrir le PDF
                          </Button>
                        </Box>
                      ) : (
                        <Box>
                          <CardMedia
                            component="img"
                            image={getRecuUrl(selectedDepense)}
                            alt="Reçu de la dépense"
                            sx={{ 
                              maxHeight: 300,
                              maxWidth: '100%',
                              objectFit: 'contain',
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(getRecuUrl(selectedDepense), '_blank')}
                          />
                          <Button
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = getRecuUrl(selectedDepense);
                              link.download = `recu_${selectedDepense.id}_${selectedDepense.date}.jpg`;
                              link.click();
                            }}
                            sx={{ mt: 2 }}
                          >
                            Télécharger l'image
                          </Button>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetailDialog}>
                Fermer
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => window.print()}
              >
                Imprimer
              </Button>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => {
                  handleCloseDetailDialog();
                  handleOpenDialog(selectedDepense);
                }}
              >
                Modifier
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Depenses;